import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, FileText } from "lucide-react";
import { toast } from "sonner";


/**
 * Bulk CSV upload button + dialog for marketplace sellers.
 * Links a "Download template" shortcut so shops copy a known-good CSV header.
 */
export default function MarketplaceBulkImport({ onImported }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get("/marketplace/bulk-import/template");
            const blob = new Blob([data.template], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "smart_marketplace_template.csv";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Could not download template. Try again.");
        }
    };

    const submit = async () => {
        if (!file) {
            toast.error("Pick a CSV file first.");
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/marketplace/bulk-import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60_000,
            });
            setResult(data);
            toast.success(`${data.inserted} listing${data.inserted === 1 ? "" : "s"} imported.`);
            onImported?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <Button
                type="button"
                variant="outline"
                onClick={() => { setOpen(true); setResult(null); setFile(null); }}
                className="rounded-sm uppercase tracking-[0.2em] text-xs h-12 border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10"
                data-testid="marketplace-bulk-import-btn"
            >
                <Upload size={14} className="mr-2" /> Bulk upload CSV
            </Button>

            <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
                <DialogContent className="bg-card border-primary/30 sm:max-w-[560px]" data-testid="bulk-import-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Bulk upload up to 50 listings</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="rounded-md border border-[#FFD700]/30 bg-[#FFD700]/5 p-3 text-xs text-amber-300/90 leading-relaxed">
                            CSV columns (first row = header): <span className="font-mono">title, description, category, condition, price_jmd, city, parish, whatsapp, is_negotiable, delivery_available, pickup_address, image_url</span>.
                            <br />
                            Image URLs can be comma-separated (up to 8 per row).
                        </div>

                        <Button
                            type="button"
                            variant="outline"
                            onClick={downloadTemplate}
                            className="w-full rounded-sm uppercase tracking-[0.2em] text-[10px] h-10"
                            data-testid="bulk-import-template-btn"
                        >
                            <Download size={12} className="mr-2" /> Download CSV template
                        </Button>

                        <label className="block cursor-pointer" data-testid="bulk-import-file-label">
                            <div className="border-2 border-dashed border-white/15 rounded-md p-6 text-center hover:border-[#FFD700]/40 transition-colors">
                                <FileText size={24} className="mx-auto mb-2 text-muted-foreground" />
                                <div className="text-sm text-foreground">
                                    {file ? file.name : "Pick a CSV file"}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
                                    .csv · max 2 MB · 50 rows
                                </div>
                            </div>
                            <input
                                type="file"
                                accept=".csv,text/csv,.tsv,.txt"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="hidden"
                                data-testid="bulk-import-file-input"
                            />
                        </label>

                        {result && (
                            <div className="bg-background border border-white/10 rounded-md p-3 text-xs" data-testid="bulk-import-result">
                                <div className="text-emerald-300 mb-1">
                                    ✓ {result.inserted} listing{result.inserted === 1 ? "" : "s"} imported.
                                </div>
                                {result.service_fee_due_jmd > 0 && (
                                    <div className="text-amber-300">
                                        Service fee due: JMD ${result.service_fee_due_jmd.toLocaleString()} (pay from My Listings).
                                    </div>
                                )}
                                {Array.isArray(result.errors) && result.errors.length > 0 && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-muted-foreground">
                                            {result.errors.length} row{result.errors.length === 1 ? "" : "s"} skipped
                                        </summary>
                                        <ul className="mt-2 text-[11px] text-red-300 space-y-1">
                                            {result.errors.slice(0, 10).map((err) => (
                                                <li key={`err-row-${err.row}`}>Row {err.row}: {err.error}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setOpen(false)}
                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-10"
                        >
                            Close
                        </Button>
                        <Button
                            type="button"
                            disabled={uploading || !file}
                            onClick={submit}
                            className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-[10px] h-10"
                            data-testid="bulk-import-submit"
                        >
                            {uploading ? "Importing…" : "Import rows"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
